import { defineStore } from "pinia";
import { listing } from "@/api";
import { ApiError, ValidationError } from "@/api/util";
import { Listing, Errors } from "@/types";

const BID_CLOSED_MESSAGE = "This auction is no longer accepting bids.";

export const useListingStore = defineStore("listingStore", () => {
  function init() {
    return {
      getListings,
      getListing,
      bid,
      cancelBid,
      getBids,
      getAccountListings,
    };
  }

  async function getListings(group: string, state: string[]) {
    let apiResponse = await listing.list(group, state);

    if (apiResponse.success) {
      return apiResponse.data.map((listing: any) => new Listing(listing));
    }

    return null;
  }

  async function getListing(slug: string) {
    let apiResponse = await listing.view(slug);

    if (apiResponse.success) {
      return new Listing(apiResponse.data);
    }

    return null;
  }

  async function bid(slug: string, amount: number, quantity: number) {
    let apiResponse = await listing.bid(slug, amount, quantity);

    if (apiResponse.success) {
      return apiResponse.data;
    }

    const error = apiResponse.error as ApiError | undefined;
    const fields = error instanceof ValidationError ? error.errors : {};
    const message =
      error?.status === 403
        ? BID_CLOSED_MESSAGE
        : error?.message ?? "An unexpected error occurred";

    throw new Errors.BidError(message, fields, error?.status);
  }

  async function cancelBid(slug: string, bidId: string) {
    let apiResponse = await listing.cancelBid(slug, bidId);

    if (apiResponse.success) {
      return apiResponse.data;
    }

    const error = apiResponse.error as ApiError | undefined;
    throw new Errors.CancelBidError(
      error?.message ?? "This bid could not be cancelled.",
      error?.status,
    );
  }

  async function getBids(slug: string) {
    let apiResponse = await listing.getBids(slug);

    if (apiResponse.success) {
      return new Listing(apiResponse.data);
    }

    throw new Errors.NotFoundError(
      apiResponse.error?.message ?? "The requested bid does not exist.",
    );
  }

  async function getAccountListings() {
    let apiResponse = await listing.getListings();

    if (apiResponse.success) {
      return apiResponse.data.map((listing: any) => new Listing(listing));
    }

    throw new Errors.NotFoundError(
      apiResponse.error?.message ?? "Could not access account listings.",
    );
  }

  return init();
});
